import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, ProjectSelect } from "@/components/common";
import { MetaStateBadge } from "@/components/MetaPanel";
import { ProjectStep, WebsiteStep, DomainStep, RequirementsStep, BlueprintStep, RfqStep, AccountsStep, MetaStep, CampaignStep, MonitorStep, OfflineStep } from "@/components/WizardSteps";

const STEPS = [
  ["project", "1. Your project", ProjectStep], ["website", "2. Website plan", WebsiteStep], ["domain", "3. Domain & emails", DomainStep],
  ["requirements", "4. Website checklist", RequirementsStep], ["blueprint", "5. Website blueprint", BlueprintStep], ["rfq", "6. Website proposal", RfqStep],
  ["accounts", "7. Business accounts", AccountsStep], ["meta", "8. Connect Meta", MetaStep], ["campaign", "9. First campaign", CampaignStep],
  ["live", "10. Results", MonitorStep], ["offline", "Offline marketing", OfflineStep],
];

function Overview({ d, go }) {
  const p = d.progress;
  const web = d.plan.answers.has_website === "yes" ? (d.plan.answers.website_verified ? "Live (verified)" : "Exists") : d.plan.website_rfq?.status === "sent" ? "Proposal requested" : d.plan.answers.website_choice ? "Planned" : "Not started";
  const items = [["Website", web], ["Meta", <MetaStateBadge key="m" state={d.meta_state === "action_required" ? "connected" : d.meta_state} />], ["Campaigns on Meta", d.campaigns.on_meta],
    ["Actual spend", inr(d.campaigns.actual_spend)], ["Actual leads", d.campaigns.actual_leads], ["Site visits / Bookings", `${d.site_visits} / ${d.bookings}`]];
  return (
    <div className="lx-card mb-6" data-testid="wizard-overview">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs font-mono text-slate-500">PROGRESS {p.done}/{p.total}</div>
          <div className="h-2 w-64 max-w-full rounded-full bg-slate-900 mt-2 overflow-hidden"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${(p.done / p.total) * 100}%` }} /></div>
        </div>
        <button onClick={() => p.next_step && go(p.next_step)} className="flex items-center gap-3 rounded-xl border border-emerald-500/40 bg-emerald-500/5 px-4 py-3 text-left hover:bg-emerald-500/10 transition-colors" data-testid="wizard-next-action">
          <div><div className="text-[10px] font-mono text-emerald-500">YOUR NEXT STEP</div><div className="font-semibold text-sm">{p.next_action}</div></div><ArrowRight className="h-4 w-4 text-emerald-500" /></button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-4">{items.map(([l, v]) => <div key={l}><div className="text-[11px] text-slate-500">{l}</div><div className="text-sm font-semibold">{v}</div></div>)}</div>
    </div>
  );
}

export default function LaunchWizard() {
  const { can } = useAuth();
  const [pid, setPid] = useState("");
  const [d, setD] = useState(null);
  const [step, setStep] = useState(null);
  const [status, setStatus] = useState("loading");
  const onProjects = (list) => setStatus(list === null ? "error" : list.length ? "ok" : "empty");
  const load = useCallback(() => pid && api.get(`/launch-plans/${pid}`).then((r) => { setD(r.data); return r.data; }).catch((e) => { toast.error(errMsg(e)); setStatus("error"); }), [pid]);
  useEffect(() => { setD(null); load()?.then?.((x) => x && setStep((s) => s || x.progress.next_step || "project")); }, [load]);
  const save = async (patch) => {
    if (!can("marketing", "write")) return toast.error("You have view-only access");
    try { await api.patch(`/launch-plans/${pid}`, patch); await load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const cur = STEPS.find((s) => s[0] === step) || STEPS[0];
  const Comp = cur[2];
  return (
    <div data-testid="launch-wizard-page">
      <PageHeader label="Launch" title="Launch My Project Marketing" subtitle="A simple step-by-step guide from website to live campaign. Your answers save automatically and you can come back any time.">
        <ProjectSelect value={pid} onChange={(v) => { setPid(v); setStep(null); }} onLoaded={onProjects} testid="wizard-project-select" />
      </PageHeader>
      {status === "empty" ? (
        <div className="lx-card text-center py-14" data-testid="wizard-no-projects">
          <div className="font-semibold">Add your first project to get started</div>
          <div className="text-sm text-slate-400 mt-1 mb-4">The launch guide works project by project. Create a project, then come back here.</div>
          <Button asChild data-testid="wizard-create-project"><Link to="/projects">Go to Projects <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
        </div>
      ) : status === "error" && !d ? (
        <div className="lx-card text-center py-14" data-testid="wizard-load-error">
          <div className="font-semibold">We couldn't load your launch plan</div>
          <Button className="mt-4" variant="outline" onClick={() => window.location.reload()} data-testid="wizard-retry">Try again</Button>
        </div>
      ) : !d ? <div className="flex items-center gap-2 text-sm text-slate-400" data-testid="wizard-loading"><Loader2 className="h-5 w-5 animate-spin" />Loading your launch plan…</div> : (
        <>
          <Overview d={d} go={setStep} />
          <div className="grid lg:grid-cols-[240px_1fr] gap-6">
            <nav className="flex lg:flex-col gap-1 overflow-x-auto custom-scrollbar pb-2" data-testid="wizard-steps-nav">
              {STEPS.map(([k, l]) => {
                const done = d.progress.steps[k];
                return (
                  <button key={k} onClick={() => setStep(k)} data-testid={`wizard-step-${k}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap text-left transition-colors ${cur[0] === k ? "bg-emerald-500/10 text-emerald-500 font-semibold" : "text-slate-300 hover:bg-slate-900"}`}>
                    {done ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" /> : <Circle className="h-4 w-4 text-slate-600 shrink-0" />}{l}
                  </button>
                );
              })}
            </nav>
            <div className="lx-card min-w-0" data-testid={`wizard-panel-${cur[0]}`}>
              <div className="text-xl font-bold mb-4">{cur[1]}</div>
              <Comp key={`${pid}-${cur[0]}`} data={d} save={save} refresh={load} canWrite={can("marketing", "write")} />
              <div className="flex justify-end mt-6">{STEPS.indexOf(cur) < STEPS.length - 1 && <Button variant="ghost" onClick={() => setStep(STEPS[STEPS.indexOf(cur) + 1][0])} data-testid="wizard-continue">Continue <ArrowRight className="h-4 w-4 ml-1" /></Button>}</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
